/**
 * Full-screen photo viewer: the item's photos one at a time, large, with
 * arrows, arrow keys, swipe, a thumbnail row, and download as JPEG or PNG.
 * Escape, the close button or a click on the backdrop closes it.
 */
import { useEffect, useRef, useState } from "react";
import { t } from "../i18n/index.ts";
import type { PhotoFormat } from "../lib/download.ts";
import { IconBack, IconDownload, IconLock, IconX } from "./icons.tsx";

export interface CarouselPhoto {
  id: string;
  url: string;
  thumbUrl: string;
  isPrivate: boolean;
  isPrimary: boolean;
}

export function PhotoCarousel({ photos, start, onClose, onDownload, busy }: {
  photos: CarouselPhoto[];
  start: number;
  onClose: () => void;
  onDownload: (photo: CarouselPhoto, format: PhotoFormat) => void;
  /** The format being downloaded, while one is. */
  busy: PhotoFormat | null;
}) {
  const [index, setIndex] = useState(Math.min(Math.max(start, 0), photos.length - 1));
  const touchX = useRef<number | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  const photo = photos[index];
  const many = photos.length > 1;
  const step = (by: number) => setIndex((i) => (i + by + photos.length) % photos.length);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, photos.length]);

  // Keep the current thumbnail in view.
  useEffect(() => {
    strip.current?.querySelector<HTMLElement>(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [index]);

  if (!photo) return null;

  return (
    <div className="carousel" role="dialog" aria-modal="true" aria-label={t("item.carousel.title")}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="carousel-top">
        <span className="carousel-count">
          {t("item.photoOf", { index: index + 1, total: photos.length })}
          {photo.isPrimary && <> · {t("item.cover")}</>}
          {photo.isPrivate && <> · <IconLock size={12} /> {t("item.gallery.privateBadge")}</>}
        </span>
        <span className="carousel-tools">
          <span className="carousel-download" role="group" aria-label={t("item.gallery.downloadThis")}>
            <IconDownload size={16} />
            {(["jpeg", "png"] as const).map((f) => (
              <button key={f} className="btn btn-sm" disabled={busy !== null} onClick={() => onDownload(photo, f)}
                title={t("item.gallery.downloadThisAs", { format: f === "jpeg" ? "JPG" : "PNG" })}>
                {busy === f ? <span className="spinner spinner-sm" /> : f === "jpeg" ? "JPG" : "PNG"}
              </button>
            ))}
          </span>
          <button className="carousel-close" onClick={onClose} aria-label={t("app.close")}><IconX /></button>
        </span>
      </div>

      <div className="carousel-stage" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          const from = touchX.current;
          const to = e.changedTouches[0]?.clientX;
          touchX.current = null;
          if (from !== null && to !== undefined && Math.abs(to - from) > 40) step(to < from ? 1 : -1);
        }}>
        {many && (
          <button className="carousel-nav is-prev" onClick={() => step(-1)} aria-label={t("item.carousel.previous")}><IconBack size={28} /></button>
        )}
        <img key={photo.id} src={photo.url} alt="" />
        {many && (
          <button className="carousel-nav is-next" onClick={() => step(1)} aria-label={t("item.carousel.next")}><IconBack size={28} /></button>
        )}
      </div>

      {many && (
        <div className="carousel-strip" ref={strip}>
          {photos.map((p, i) => (
            <button key={p.id} data-index={i} aria-current={i === index} onClick={() => setIndex(i)}
              aria-label={t("item.photoOf", { index: i + 1, total: photos.length })}>
              <img src={p.thumbUrl} alt="" loading="lazy" />
              {p.isPrivate && <span className="carousel-strip-lock"><IconLock size={10} /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
